import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import ContactSection from "./ContactSection";
import Footer from "./Footer";
import FooterMobile from "./FooterMobile";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

interface SlidingUpRevealProps {
  backgroundColor?: string;
  yPercent?: number;
}

function getResponsiveYPercent() {
  const vw = window.innerWidth;
  const minVW = 320; // mobile
  const maxVW = 1920; // desktop
  const minY = -40; // yPercent at smallest screen (mobile)
  const maxY = -73; // yPercent at largest screen (desktop)

  const clampedVW = Math.min(Math.max(vw, minVW), maxVW);
  return minY + (maxY - minY) * ((clampedVW - minVW) / (maxVW - minVW));
}

const GarageSlider: React.FC<SlidingUpRevealProps> = ({
  backgroundColor = "#0d1821",
  yPercent = getResponsiveYPercent(),
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let createdSmoother = false;
    let smoother = ScrollSmoother.get();
    const shouldSmooth = window.matchMedia("(min-width: 768px)").matches;

    if (!shouldSmooth) {
      smoother?.kill();
      smoother = undefined;
    } else if (!smoother) {
      smoother = ScrollSmoother.create({
        smooth: 0.5,
        effects: true,
      });
      createdSmoother = true;
    }

    const ctx = gsap.context(() => {
      ScrollTrigger.matchMedia({
        // Desktop → pinned animation
        "(min-width: 768px)": () => {
          gsap.to(topRef.current, {
            yPercent,
            ease: "none",
            scrollTrigger: {
              trigger: containerRef.current,
              start: "top top",
              end: "+=1500",
              scrub: true,
              pin: true,
            },
          });
        },

        // Mobile → NO pin, just scrub + spacer for extra scroll
        "(max-width: 767px)": () => {
          gsap.to(topRef.current, {
            yPercent,
            ease: "none",
            scrollTrigger: {
              trigger: containerRef.current,
              start: "top top",
              end: "+=1500", // shorter scroll length for mobile
              scrub: true,
              pin: false,
            },
          });
        },
      });
    }, containerRef);

    return () => {
      ctx.revert();
      if (createdSmoother) {
        smoother?.kill();
      }
    };
  }, [yPercent]);

  return (
    <>
      <section
        ref={containerRef}
        className="relative w-full min-h-screen overflow-hidden"
      >
        {/* Bottom Content */}
        <div
          ref={bottomRef}
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor }}
        >
          <Footer />
        </div>

        {/* Top Sliding Layer (sticky) */}
        <div
          ref={topRef}
          className="
            sticky top-0 h-screen flex flex-col items-center justify-center bg-[#f5f5f5] rounded-br-[90px] sm:rounded-br-[110px] lg:rounded-br-[150px] px-4 sm:px-6 md:px-12 py-10 sm:py-16"
        >
          <div className="w-full">
            <ContactSection />
          </div>
        </div>
      </section>

      <div className="md:hidden">
        <FooterMobile />
      </div>
    </>
  );
};

export default GarageSlider;
